import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import AppLayout from '../../components/layouts/AppLayout.js'
import { useTranslation } from 'next-i18next'
import PluginStyle from '../../styles/modules/plugins.module.scss'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { useRouter } from 'next/router'
import ServicesToolbar from '../../components/services/ServicesToolbar'
import Pagination from '../../components/Pagination'
import Link from 'next/link'
import Image from 'next/future/image'
import ServicesFilterSearch from '../../components/services/ServicesFilterSearch'
import { client } from '../../services/client'
import { GET_SERVICES } from '../../services/graphql/queries/services'
import ServicesFilter from '../../components/services/ServicesFilter'
import useTranslatedRoutes from '../../services/translatedRoutes'

export default function Services ({ hasServicesQuery, services }) {
  const router = useRouter()
  const { t } = useTranslation('services')
  const [companies, setCompanies] = useState({
    data: [], meta: {}, links: []
  })
  const language = router.locale.toUpperCase()
  const { getServiceName } = useTranslatedRoutes(language)
  const [query, updateQuery] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      sort: router.query.sort ?? 'NEWEST',
      page: router.query.page ?? 1,
      search: router.query.search ?? '',
      filter: router.query.filter ? JSON.parse(router.query.filter) : { services: [] }
    }
  )

  const title = useMemo(() => {
    const { service } = router.query

    if (service) {
      return t(`enum.${getServiceName(decodeURI(service))}`, { ns: 'services' })
    }

    return t('title')
  }, [router.query, t, getServiceName])

  const buildFilter = () => {
    const queryFilter = query.filter
    /**
     * Those are the special cases, where we need to add the application or category to the filter
     * This is because someone clicked the application filter from the landing-page
     */
    if (hasServicesQuery) {
      const serviceName = getServiceName(decodeURI(router.query.service))

      queryFilter.services = [serviceName]
    }

    /**
     * This probably only occurs, when the user uses the header search bar and is on the plugin page
     */
    if (router.query.search && router.query.search !== query.search) {
      query.search = router.query.search
    }

    return new URLSearchParams({
      ...query,
      filter: JSON.stringify(queryFilter)
    })
  }

  useEffect(() => {
    if (Object.keys(query.filter).length === 0) return

    const path = router.asPath.split('?')[0]
    const params = buildFilter()

    router.replace(`${path}?${params.toString()}`, undefined, { shallow: true })
  }, [query.sort, query.page, query.filter])

  useEffect(() => {
    if (!query.search) return

    updateQuery({
      search: query.search,
      filter: { services: [] },
      page: 1,
      sort: 'NEWEST'
    })
  }, [query.search])

  const searchServices = async () => {
    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL
    const params = new URLSearchParams(router.query)

    const response = await fetch(`${baseUrl}api/services/search?${params.toString()}`)
    return response.json()
  }

  useEffect(() => {
    let isSearching = true

    /**
     * If you are on the product site and select a plugin per software, then it won't use the useEffect to build up
     * the url properly. So we need to do it here.
     */
    if (!router.query.sort) {
      const path = router.asPath.split('?')[0]
      const params = buildFilter()

      router.replace(`${path}?${params.toString()}`, undefined, { shallow: true })

      return
    }

    // Push search event if term is given
    if (query.search) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'search',
        searchTerm: query.search
      })
    }

    // Trigger search
    searchServices().then(data => {
      if (!isSearching) return

      setCompanies(data)
    })

    return () => {
      isSearching = false
    }
  }, [router.query])

  return (
    <AppLayout>
      <Head>
        <title>{t('meta.title')}</title>
      </Head>
      <div className="appendix appendix--bottomright appendix--turquoise">
        <div className={`container rounded bg-light-blue p-5 ${PluginStyle.banner}`}>
          <h1 className="text-dark-blue">
            { title }
          </h1>
        </div>
        <div className="container py-5">
          <div className="row g-md-5 justify-content-between">
            <div className="col-12 col-lg-3">
              <ServicesFilterSearch
                search={query.search}
                onSearchChange={e => updateQuery({ search: e })}
                placeholder={t('browse_all.filter.search')}
              />
              <ServicesFilter
                data={services}
                onCheckedServiceChange={e => {
                  updateQuery({ filter: { ...query.filter, services: e }, page: 1 })
                }}
                checkedServices={query.filter.services}
                companies={companies}
              />
            </div>
            <div className="col-lg-8 col-12">
              <div className="row">
                <div className="col-12 mb-3">
                  <ServicesToolbar
                    companies={companies}
                    onSortChange={e => updateQuery({ sort: e })}
                    sort={query.sort}
                  />
                </div>
                {
                  companies.data.map(company => (
                    <div
                      className="col-lg-6 col-12 plugincard"
                      key={company.id}
                    >
                      <Link href={`/seller/${company.slug}`}>
                        <a
                          className="border border-light h-100 d-flex flex-column rounded-3 overflow-hidden cursor-pointer text-decoration-none text-default"
                        >
                          <div className="row">
                            <div className="d-flex align-items-center justify-content-center ">
                              <div
                                className="d-flex align-items-center justify-content-center p-5 cursor-pointer bg-white w-100"
                                style={{ minHeight: '250px' }}
                              >
                                {
                                  company.logo && (
                                    <Image
                                      alt=""
                                      className="w-auto h-auto d-block"
                                      src={company.logo.url}
                                      width={370}
                                      height={250}
                                    />
                                  )}
                                {
                                  !company.logo && t('plugins.no_image')
                                }
                              </div>
                            </div>
                          </div>
                          <div className="bg-light p-4 flex-grow-1 d-flex flex-column">
                            <div className="d-flex flex-wrap fs-6 gap-2 mb-2">
                            </div>
                            <div className="d-flex flex-column align-items-start flex-grow-1">
                              <span className="text-dark text-decoration-none cursor-pointer">
                                <h3>{company.name}</h3>
                              </span>
                              {
                                (company.descriptions.length > 0) && (
                                  <p className="mb-0 flex-grow-1">
                                    {
                                      company.descriptions[0].description.length > 255
                                        ? company.descriptions[0].description.substring(0, 255) + '...'
                                        : company.descriptions[0].description.length > 0
                                          ? company.descriptions[0].description
                                          : t('no_description')
                                    }
                                  </p>
                                )
                              }
                              <div className="row g-2">
                                {
                                  company.services.map(service => (
                                    <div
                                      key={service.id}
                                      className="col-auto"
                                    >
                                      <div
                                        className="badge text-primary rounded-pill"
                                        style={{ backgroundColor: 'rgba(107, 113, 119, 0.1)' }}
                                      >
                                        {t(`enum.${service.service}`, { ns: 'services' })}
                                      </div>
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          </div>
                        </a>
                      </Link>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
          {
            companies.data.length > 0 && (
              <div className="row mt-5">
                <Pagination
                  paginatorInfo={{ lastPage: companies?.meta?.last_page }}
                  page={query.page}
                  onPageChange={e => updateQuery({ page: e })}
                  className={'d-flex justify-content-center'}
                />
              </div>
            )}
        </div>
      </div>
    </AppLayout>
  )
}

export async function getServerSideProps ({ locale, query }) {
  const newClient = client(locale)

  const { data: services } = await newClient.query({
    fetchPolicy: 'no-cache',
    query: GET_SERVICES
  })

  return {
    props: {
      services,
      hasServicesQuery: !!query.service,
      ...(await serverSideTranslations(locale, ['languages', 'footer', 'header', 'common', 'routes', 'services']))
    }
  }
}
